import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AnalyticsFilterBar,
  AnalyticsFilters,
} from "../components/AnalyticsPlus/AnalyticsFilterBar";
import {
  AnalyticsAudience,
  AnalyticsPlatform,
} from "../api/analyticsAdmin";
import {
  computeDateRange,
  DateRangePreset,
} from "../helpers/analyticsPlus";
import OverviewTab from "../components/AnalyticsPlus/OverviewTab";
import UsersTab from "../components/AnalyticsPlus/UsersTab";
import ActivityNavTabs from "../components/AnalyticsPlus/ActivityNavTabs";
import PlatformPointBalanceCard from "../components/AnalyticsPlus/PlatformPointBalanceCard";

interface AnalyticsPlusTab {
  key: string;
  label: string;
  component: React.FC<{ filters: AnalyticsFilters }>;
}

const Placeholder: React.FC<{ filters: AnalyticsFilters }> = ({ filters }) => (
  <div className="text-sm text-gray-500">
    Coming soon ({filters.from} → {filters.to})
  </div>
);

const TABS: AnalyticsPlusTab[] = [
  { key: "overview", label: "Visitors", component: OverviewTab },
  { key: "users", label: "Users", component: UsersTab },
];

const AdminAnalyticsPlus: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [preset, setPreset] = useState<DateRangePreset>("last30");
  const initialRange = useMemo(() => computeDateRange("last30", new Date()), []);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    from: initialRange.from,
    to: initialRange.to,
    platform: "All",
    audience: "All",
  });

  const handlePresetChange = (next: DateRangePreset) => {
    setPreset(next);
    if (next !== "custom") {
      const range = computeDateRange(next, new Date());
      setFilters((prev) => ({ ...prev, from: range.from, to: range.to }));
    }
  };

  const handleCustomChange = (
    patch: Partial<Pick<AnalyticsFilters, "from" | "to">>
  ) => setFilters((prev) => ({ ...prev, ...patch }));

  const handlePlatformChange = (platform: AnalyticsPlatform) =>
    setFilters((prev) => ({ ...prev, platform }));

  const handleAudienceChange = (audience: AnalyticsAudience) =>
    setFilters((prev) => ({ ...prev, audience }));

  const requested = searchParams.get("tab");
  const activeTab = TABS.find((t) => t.key === requested) ?? TABS[0];
  const ActiveComponent = activeTab.component;

  return (
    <div>
      <PlatformPointBalanceCard />

      <h2 className="font-medium mb-1">Activity</h2>
      <p className="text-sm text-gray-500 mb-6">
        Activity metrics have been collected since 22 June 2026, so earlier
        dates show no data.
      </p>

      <ActivityNavTabs />

      <AnalyticsFilterBar
        filters={filters}
        preset={preset}
        onPresetChange={handlePresetChange}
        onCustomChange={handleCustomChange}
        onPlatformChange={handlePlatformChange}
        onAudienceChange={handleAudienceChange}
      />

      <div className="flex flex-wrap gap-1 mb-8 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSearchParams({ tab: tab.key })}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab.key === activeTab.key
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ActiveComponent filters={filters} />
    </div>
  );
};

export default AdminAnalyticsPlus;
